import { useEffect, useState } from 'react';
import { Sheet } from './sheet.js';
import { SortDirectionToggle, type SortDirection } from './sort-direction.js';
import { api, call } from '../platform/api.js';
import { OptionSheet } from './option-sheet.js';
import { countFilters, STATUS_OPTIONS, type ActiveFilters } from '../screens/item-filters.js';
// The null sentinels are the BOXES sheet's, imported rather than re-declared — one string for
// "no source yet" across the app (DRY: a fact, hoisted at the first duplicate).
import { FILTER_NO_SOURCE, FILTER_NO_KIND } from '../screens/box-filters.js';
import { NO_KIND_LABEL, SOURCE_KIND_VALUES, SOURCE_KIND_LABELS } from '@mononoke/shared';
import type { IntakeMeta } from '../../../api/src/catalog/intake.js';
import { readIntakeMeta } from '../platform/intake-meta.js';
import type { ItemSort } from '../../../api/src/catalog/items.js';
import type { SourceRow } from '../../../api/src/catalog/sources.js';

/** The sort axes (server `ITEM_SORTS`) with their sheet labels. The inline Sort dropdown that
 *  carried these (0.1.345) moved in here, below Status, so the whole view is set in one place. */
/**
 * Each axis carries the direction it READS as (0.1.394) — "Recently added" promises newest first,
 * which on a date axis is descending, while a name promises A→Z. Picking a sort seeds the toggle
 * with that promise, so the label and the list agree the moment you choose it; the toggle then
 * flips it for anyone who wants the other end.
 */
const SORT_OPTIONS: { value: ItemSort; label: string; natural: SortDirection }[] = [
  { value: 'name', label: 'Name', natural: 'asc' },
  { value: 'recent', label: 'Recently added', natural: 'desc' },
  { value: 'status', label: 'Status', natural: 'asc' },
];

/**
 * THE FILTER SHEET (frame 3c — the behaviour change, 0.1.314). One page, not the old two-stage
 * pick-a-dimension-then-a-value picker: STATUS is multi-select pills, "No photo yet" is a
 * checkbox, and Category / the SELECT-kind fields are rows that open a picker and show what is
 * chosen. It edits a DRAFT and commits on "Show N" — nothing changes the list until then, which
 * is what lets the footer read a LIVE count of what applying would show ("412 match" → "Show 412").
 *
 * ⚠️ THE DRAFT IS SEEDED FROM THE APPLIED FILTERS and only replaces them on apply, so opening the
 * sheet, toggling, and closing without "Show" leaves the list exactly as it was. `countFor` is the
 * parent's own list query (it owns the scope — box vs catalogue — and the search), asked with the
 * draft, so the count cannot disagree with what applying actually produces.
 */
export function FilterSheet({
  filters,
  sort,
  direction,
  countFor,
  onApply,
  onClose,
  elevated = false,
  hideSort = false,
}: {
  filters: ActiveFilters;
  /** The current sort axis, seeded into the draft; committed with the filters on "Show N". */
  sort: ItemSort;
  direction: SortDirection;
  countFor: (draft: ActiveFilters) => Promise<number | null>;
  onApply: (next: ActiveFilters, sort: ItemSort, direction: SortDirection) => void;
  onClose: () => void;
  /** Opened from inside the phone Display sheet (P3): sit above it — its scrim would cover this. */
    elevated?: boolean;
  /** The Display sheet owns sort on the phone list: no second, silent sort here. */
  hideSort?: boolean;
}): JSX.Element {
  const [meta, setMeta] = useState<IntakeMeta | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [draft, setDraft] = useState<ActiveFilters>(filters);
  /** Sort is not part of `ActiveFilters` (it does not change the COUNT), so it rides its own draft
   *  and commits alongside the filters on "Show N" — the sheet's "nothing changes until Show" rule. */
  const [draftSort, setDraftSort] = useState<ItemSort>(sort);
  const [draftDir, setDraftDir] = useState<SortDirection>(direction);
  /** The live "N match" count for the current draft; null while unknown/loading. */
  const [count, setCount] = useState<number | null>(null);
  /** Which field/category picker is open over the sheet. */
  const [picking, setPicking] = useState<'category' | { key: string } | null>(null);
  /** The tenant's sources, for the Source-name pills (068). `[]` until read, and on a failure. */
  const [sources, setSources] = useState<SourceRow[]>([]);

  useEffect(() => {
    void (async () => {
      const result = await readIntakeMeta();
      if (result.ok) setMeta(result.data);
      else setProblem(result.failure.message);
    })();
  }, []);

  /**
   * THE SOURCE LIST, for the Source-name pills (0.1.395). Its own read rather than a new field on
   * `intakeMeta`: that read backs the intake FORM's vocabulary and is asked for on several screens,
   * and widening it to carry a filter's options would make every one of them pay for this sheet.
   *
   * A failure is deliberately quiet — the section simply does not render, and `call` has ledgered
   * it. Shouting here would put an alert over a sheet whose other five sections work.
   */
  useEffect(() => {
    void (async () => {
      const result = await call('listSources', () => api.listSources.query());
      if (result.ok) setSources(result.data);
    })();
  }, []);

  // The live count, debounced so a run of pill taps is one query, not one each. Re-reads whenever
  // the draft changes; a failed count leaves the last known number rather than flashing to zero.
  useEffect(() => {
    let live = true;
    const handle = setTimeout(() => {
      void (async () => {
        const n = await countFor(draft);
        if (live && n !== null) setCount(n);
      })();
    }, 200);
    return () => {
      live = false;
      clearTimeout(handle);
    };
  }, [draft, countFor]);

  if (problem !== null) {
    return (
      <Sheet label="Filter" onClose={onClose} className="filter-sheet" elevated={elevated}>
        <p role="alert">{problem}</p>
      </Sheet>
    );
  }

  const statuses = draft.status ?? [];
  const toggleStatus = (value: string): void => {
    setDraft((d) => {
      const set = new Set(d.status ?? []);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      const next = [...set];
      return { ...d, status: next.length > 0 ? next : undefined };
    });
  };

  const selectFields = (meta?.fields ?? []).filter((f) => f.kind === 'single_select' || f.kind === 'multi_select');
  const on = countFilters(draft);

  /** The two source dimensions toggle exactly like Status: a set, empty meaning "no filter". */
  const toggleIn = (key: 'sources' | 'sourceKinds', value: string): void => {
    setDraft((d) => {
      const set = new Set(d[key] ?? []);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      const next = [...set];
      return { ...d, [key]: next.length > 0 ? next : undefined };
    });
  };
  const pillFor = (key: 'sources' | 'sourceKinds', value: string, label: string): JSX.Element => {
    const lit = (draft[key] ?? []).includes(value);
    return (
      <button
        key={value}
        type="button"
        className={lit ? 'filter-pill filter-pill-on' : 'filter-pill'}
        aria-pressed={lit}
        onClick={() => toggleIn(key, value)}
      >
        {label}
      </button>
    );
  };
  const sourcePill = (value: string, label: string): JSX.Element => pillFor('sources', value, label);
  const kindPill = (value: string, label: string): JSX.Element => pillFor('sourceKinds', value, label);

  return (
    <Sheet label="Filter" onClose={onClose} className="filter-sheet" elevated={elevated}>
      <header className="filter-head">
        <strong>Filter</strong>
        {on > 0 && <span className="filter-on">{on} on</span>}
        <span className="filter-head-spacer" aria-hidden="true" />
        {!hideSort && <SortDirectionToggle value={draftDir} onChange={setDraftDir} />}
        <button type="button" className="linklike" disabled={on === 0} onClick={() => setDraft({ fields: {} })}>
          Clear all
        </button>
      </header>

      <div className="filter-body">
        {/* STATUS AND No-photo DO NOT WAIT ON THE NETWORK: their options are static (the frame's
            five pills, a predicate checkbox), and status is the filter an atelier opens this
            sheet FOR. Only Category and the select fields draw from `intakeMeta`, so only they
            hold for it — the sheet is useful the instant it opens. */}
        <section className="filter-section">
          <h3 className="section-head">Status</h3>
          <div className="filter-pills">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s.value}
                type="button"
                className={statuses.includes(s.value) ? 'filter-pill filter-pill-on' : 'filter-pill'}
                aria-pressed={statuses.includes(s.value)}
                onClick={() => toggleStatus(s.value)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </section>

        {/* SORT — single-select, below Status (0.1.345). The inline Sort dropdown that used to sit
            in the head row lives here now; picking one lights it and commits on "Show N". */}
        {!hideSort && (
          <section className="filter-section">
            <h3 className="section-head">Sort</h3>
            <div className="filter-pills">
              {SORT_OPTIONS.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  className={draftSort === s.value ? 'filter-pill filter-pill-on' : 'filter-pill'}
                  aria-pressed={draftSort === s.value}
                  onClick={() => {
                    setDraftSort(s.value);
                    setDraftDir(s.natural);
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </section>
        )}

        {/**
         * SOURCE NAME AND SOURCE TYPE (0.1.395, maintainer 26 Aug) — the two sections the BOXES
         * sheet has carried since 0.1.370, now over the item's OWN source (068) rather than its
         * parcel's. Same shape deliberately: pills, the null case first, and the sentinels
         * imported from the box module so "no source yet" is one string in this app, not two.
         *
         * ⚠️ ONLY THE SOURCES THAT EXIST ARE OFFERED, and the section is absent entirely while the
         * list is unread or empty — a pill that answers with nothing reads as a broken screen
         * rather than an honest zero (the boxes sheet's rule). The "not set" pills are always
         * offered when there ARE sources, because an unanswered question is exactly the thing
         * somebody opens a filter to chase, and 073 made "no type" a real answer.
         */}
        {sources.length > 0 && (
          <section className="filter-section">
            <h3 className="section-head">Source name</h3>
            <div className="filter-pills">
              {sourcePill(FILTER_NO_SOURCE, 'No source yet')}
              {sources.map((s) => sourcePill(s.id, s.name))}
            </div>
          </section>
        )}

        <section className="filter-section">
          <h3 className="section-head">Source type</h3>
          <div className="filter-pills">
            {kindPill(FILTER_NO_KIND, NO_KIND_LABEL)}
            {SOURCE_KIND_VALUES.map((k) => kindPill(k, SOURCE_KIND_LABELS[k]))}
          </div>
        </section>

        {/* A PREDICATE, NOT A DIMENSION (field report, 30 Jul): a checkbox, not a picker. */}
        <label className="filter-check">
          <input
            type="checkbox"
            checked={draft.withoutPhoto === true}
            onChange={(e) => {
              // ⚠️ READ THE EVENT BEFORE QUEUEING THE UPDATE (MONONOKE-WEB-1V, 0.1.460). React nulls
              // `currentTarget` the moment this handler returns, and the updater below does not run
              // now — it runs at the next render whenever another update is already queued (a second
              // tap in the same batch, a live count arriving). Until 0.1.460 the updater read
              // `e.currentTarget.checked` itself: fine on a lone tap, which React evaluates eagerly,
              // and a null read that took the whole Items screen down on the tap after it (16 Aug
              // 2026). `e.target` survives; `e.currentTarget` does not. Same rule for every handler.
              const checked = e.currentTarget.checked;
              setDraft((d) => ({ ...d, withoutPhoto: checked ? true : undefined }));
            }}
          />
          No photo yet
        </label>

        {/* 0.1.515 — the barcode backlog, the same kind of predicate as the photo one above. */}
        <label className="filter-check">
          <input
            type="checkbox"
            checked={draft.withoutBarcode === true}
            onChange={(e) => {
              // Read before queueing — the MONONOKE-WEB-1V rule spelled out on the photo box above.
              const checked = e.currentTarget.checked;
              setDraft((d) => ({ ...d, withoutBarcode: checked ? true : undefined }));
            }}
          />
          No barcode yet
        </label>

        {meta === null ? (
          <p className="filter-row" role="status">
            Loading categories…
          </p>
        ) : (
          <>
            <button
              type="button"
              className="filter-row"
              aria-label={draft.category ? `Category: ${draft.category.path}` : 'Category: any'}
              onClick={() => setPicking('category')}
            >
              <span className="filter-row-label">Category</span>
              {draft.category ? (
                <span className="filter-row-value filter-row-value-on">{draft.category.path}</span>
              ) : (
                <span className="filter-row-value">any</span>
              )}
              <span className="filter-row-chevron" aria-hidden="true">
                ›
              </span>
            </button>

            {selectFields.map((field) => (
              <button
                key={field.key}
                type="button"
                className="filter-row"
                aria-label={draft.fields[field.key] ? `${field.label}: ${draft.fields[field.key]!.value}` : `${field.label}: any`}
                onClick={() => setPicking({ key: field.key })}
              >
                <span className="filter-row-label">{field.label}</span>
                {draft.fields[field.key] ? (
                  <span className="filter-row-value filter-row-value-on">{draft.fields[field.key]!.value}</span>
                ) : (
                  <span className="filter-row-value">any</span>
                )}
                <span className="filter-row-chevron" aria-hidden="true">
                  ›
                </span>
              </button>
            ))}
          </>
        )}
      </div>

      <footer className="filter-foot">
        <span className="filter-match">{count === null ? '…' : `${count.toLocaleString('en-US')} match`}</span>
        <button type="button" className="filter-show" onClick={() => onApply(draft, draftSort, draftDir)}>
          {count === null ? 'Show' : `Show ${count.toLocaleString('en-US')}`}
        </button>
      </footer>

      {/* The dimension pickers open OVER the sheet (OptionSheet z 11 > sheet z 10). Picking sets
          the draft and closes only the picker — the sheet stays, so the live count updates. */}
      {picking === 'category' && meta !== null && (
        <OptionSheet
          title="Category"
          options={meta.categories.map((c) => c.path)}
          selected={draft.category ? [draft.category.path] : []}
          multi={false}
          onChange={([path]) => {
            setDraft((d) => {
              if (path === undefined) return { ...d, category: undefined };
              const found = meta.categories.find((c) => c.path === path);
              return found ? { ...d, category: { id: found.id, path: found.path } } : { ...d, category: undefined };
            });
            setPicking(null);
          }}
          onClose={() => setPicking(null)}
        />
      )}
      {picking !== null && picking !== 'category' && meta !== null && (
        <OptionSheet
          title={meta.fields.find((f) => f.key === picking.key)?.label ?? picking.key}
          options={meta.fields.find((f) => f.key === picking.key)?.options ?? []}
          selected={draft.fields[picking.key] ? [draft.fields[picking.key]!.value] : []}
          multi={false}
          // Free entry: values free-typed at the rack exist on items without being options.
          allowFree
          onChange={([value]) => {
            const key = picking.key;
            const field = meta.fields.find((f) => f.key === key);
            setDraft((d) => {
              const fields = { ...d.fields };
              if (value === undefined || field === undefined) delete fields[key];
              else fields[key] = { label: field.label, value };
              return { ...d, fields };
            });
            setPicking(null);
          }}
          onClose={() => setPicking(null)}
        />
      )}
    </Sheet>
  );
}
